/**
 * Rétention de l'audio du Coach Debrief — 90 jours.
 *
 * ⚠️ Ce n'est PAS un réglage technique : c'est un ENGAGEMENT ÉCRIT pris envers les clients.
 * Le Dossier de Confiance annonce « rétention limitée des données sensibles (audio Coach
 * Debrief : 90 jours) » (`src/lib/tech-doc-content.ts`, deux endroits). Or le code ne
 * supprimait jamais rien : `deleteAudio` (`storage.service.ts`) existait, son commentaire
 * promettait un « cron de purge rétention »… et la fonction n'avait AUCUN appelant dans tout
 * le dépôt. Chaque enregistrement de rendez-vous réel déposé depuis la mise en service restait
 * indéfiniment sur R2 — des conversations avec des tiers qui ne sont pas nos utilisateurs.
 *
 * Changer la durée ici ne suffit donc pas : il faut aussi corriger le Dossier de Confiance,
 * sinon l'un des deux mentira. Garde-fou : `tests/purge-audio-retention.test.ts` lit la durée
 * dans CE fichier et dans le Dossier, et refuse qu'elles divergent.
 *
 * Ce qui est supprimé : le FICHIER AUDIO sur R2, et lui seul. La transcription, le débrief,
 * les notes et les extraits restent — c'est la valeur pédagogique de la séance, et c'est ce que
 * l'engagement visait (« données sensibles »). Supprimer aussi le texte serait une autre
 * décision, à prendre par Valérie.
 */

/** Durée de conservation de l'enregistrement, en jours. Source : Dossier de Confiance. */
export const AUDIO_RETENTION_DAYS = 90;

/** Nom de la variable d'armement. Sans elle, la purge ne supprime RIEN. */
export const VARIABLE_ACTIVATION_PURGE = "AUDIO_RETENTION_ENABLED";

/** Tout enregistrement déposé avant cette date est hors rétention. */
export function dateLimiteRetention(now: Date = new Date()): Date {
  return new Date(now.getTime() - AUDIO_RETENTION_DAYS * 24 * 60 * 60 * 1000);
}

/**
 * L'enregistrement de cette séance a-t-il été supprimé par la purge ?
 *
 * Déduit, sans colonne dédiée : une séance créée depuis un fichier audio ou une vidéo
 * (`inputType !== "transcript"`) qui n'a plus de clé de stockage a forcément été purgée.
 * Sans cette distinction, le lecteur audio disparaîtrait de l'écran sans explication et
 * serait pris pour une panne — alors qu'une séance saisie au clavier n'a, elle, jamais eu
 * d'enregistrement et ne doit afficher aucun message.
 */
export function enregistrementPurge(seance: {
  inputType?: string | null;
  audioStorageKey?: string | null;
  transcriptUtterances?: unknown;
}): boolean {
  if (seance.audioStorageKey) return false;
  // ⚠️ On exige une preuve POSITIVE qu'un enregistrement a existé. Se contenter de
  // `inputType !== "transcript"` ferait afficher « enregistrement supprimé » sur toute séance
  // saisie au clavier dès que ce champ n'est pas transmis — et l'écran de débrief ne le reçoit
  // pas (il n'est pas dans le type exposé au navigateur).
  const depuisUnFichier = seance.inputType === "audio" || seance.inputType === "video";
  // Les segments par locuteur ne peuvent venir que de Deepgram, donc d'un audio : leur présence
  // prouve l'enregistrement même sans `inputType`. C'est ce que lit l'écran de débrief.
  const aDesSegments = Array.isArray(seance.transcriptUtterances) && seance.transcriptUtterances.length > 0;
  return depuisUnFichier || aDesSegments;
}

export interface DecisionPurge {
  /** La tâche doit-elle s'exécuter ? */
  doitTourner: boolean;
  /** A-t-elle le droit de SUPPRIMER ? Faux en mode à blanc comme sans la variable. */
  supprime: boolean;
  /** Message à journaliser quand elle ne fait rien. */
  message?: string;
}

/**
 * Armement de la purge — même exigence que la réconciliation Zoho (Valérie, 29/09/2026) :
 * une tâche qui change l'état sans que personne l'ait demandé ne doit pas pouvoir tourner
 * avant qu'on ait lu sa sortie à blanc sur chaque environnement. Ici l'enjeu est plus fort
 * encore : la suppression d'un fichier sur R2 est IRRÉVERSIBLE, et au premier passage elle
 * porterait sur tout l'historique d'un coup.
 *
 * ⚠️ `=== "true"` exactement : ni « 1 », ni « yes », ni « TRUE ». Une valeur approchante
 * laisse la tâche désarmée — on ne devine jamais une autorisation de supprimer.
 */
export function decisionPurgeAudio(args: {
  dryRun: boolean;
  activation: string | undefined;
}): DecisionPurge {
  const armee = args.activation === "true";
  if (args.dryRun) return { doitTourner: true, supprime: false };
  if (!armee) {
    return {
      doitTourner: false,
      supprime: false,
      message:
        `[purge-audio] DÉSARMÉE : ${VARIABLE_ACTIVATION_PURGE} n'est pas à « true », aucun ` +
        `enregistrement n'est supprimé. Lancer d'abord « bun run scripts/cron-purge-audio-coach-debrief.ts --dry-run » ` +
        `et faire lire sa sortie avant d'armer.`,
    };
  }
  return { doitTourner: true, supprime: true };
}
